import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, AlertTriangle, Upload, FileText, GraduationCap, Users, Edit, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Disciplinary() {
  const { user } = useAuth();
  const [studentIncidents, setStudentIncidents] = useState([]);
  const [teacherIncidents, setTeacherIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [formData, setFormData] = useState({
    incident_type: 'student',
    subject_id: '',
    subject_name: '',
    description: '',
    severity: 'low',
    action_taken: '',
    follow_up: '',
  });
  const [editFormData, setEditFormData] = useState({
    subject_id: '',
    subject_name: '',
    description: '',
    severity: 'low',
    action_taken: '',
    follow_up: '',
    status: 'open'
  });
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      if (user.role === 'student' || user.role === 'teacher') {
        // Users only see their own incidents
        const { data } = await axios.get(`${BACKEND_URL}/api/disciplinary`, {
          withCredentials: true,
        });
        if (user.role === 'student') {
          setStudentIncidents(data);
        } else {
          setTeacherIncidents(data);
        }
      } else {
        // Admin/Board Member sees both
        const [studentData, teacherData] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/disciplinary?incident_type=student`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/disciplinary?incident_type=teacher`, { withCredentials: true }),
        ]);
        setStudentIncidents(studentData.data);
        setTeacherIncidents(teacherData.data);
      }
    } catch (error) {
      console.error('Error fetching incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/disciplinary`, formData, {
        withCredentials: true,
      });
      
      // Upload attachment if provided
      if (attachmentFile && data.id) {
        setUploadingFile(true);
        const fileFormData = new FormData();
        fileFormData.append('file', attachmentFile);
        
        await axios.post(
          `${BACKEND_URL}/api/disciplinary/${data.id}/attachments`,
          fileFormData,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        setUploadingFile(false);
      }
      
      toast.success('Disciplinary incident created successfully');
      setDialogOpen(false);
      setFormData({
        incident_type: 'student',
        subject_id: '',
        subject_name: '',
        description: '',
        severity: 'low',
        action_taken: '',
        follow_up: '',
      });
      setAttachmentFile(null);
      fetchIncidents();
    } catch (error) {
      setUploadingFile(false);
      toast.error(error.response?.data?.detail || 'Failed to create incident');
    }
  };

  const openEditDialog = (incident) => {
    setSelectedIncident(incident);
    setEditFormData({
      subject_id: incident.subject_id,
      subject_name: incident.subject_name,
      description: incident.description,
      severity: incident.severity,
      action_taken: incident.action_taken,
      follow_up: incident.follow_up || '',
      status: incident.status
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${BACKEND_URL}/api/disciplinary/${selectedIncident.id}`,
        editFormData,
        { withCredentials: true }
      );
      toast.success('Incident updated successfully');
      setEditDialogOpen(false);
      fetchIncidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update incident');
    }
  };

  const openViewDialog = (incident) => {
    setSelectedIncident(incident);
    setViewDialogOpen(true);
  };

  const handleDelete = async (incidentId) => {
    if (!window.confirm('Are you sure you want to delete this incident? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/disciplinary/${incidentId}`,
        { withCredentials: true }
      );
      toast.success('Incident deleted successfully');
      fetchIncidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete incident');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setAttachmentFile(file);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div data-testid="disciplinary-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1A202C]" style={{ fontFamily: 'Manrope' }}>
            Disciplinary Matters
          </h1>
          <p className="text-[#718096] mt-2">Track and manage disciplinary incidents</p>
        </div>
        {(user.role === 'admin' || user.role === 'board_member' || user.role === 'teacher') && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-incident-btn" className="bg-gradient-to-r from-[#667EEA] to-[#764BA2] hover:from-[#764BA2] hover:to-[#667EEA] shadow-lg">
                <Plus className="w-4 h-4 mr-2" />
                Add Incident
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add Disciplinary Incident</DialogTitle>
                <DialogDescription>Record a new disciplinary incident</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="incident_type">Incident Type</Label>
                  <Select
                    value={formData.incident_type}
                    onValueChange={(value) => setFormData({ ...formData, incident_type: value, subject_id: '', subject_name: '' })}
                  >
                    <SelectTrigger data-testid="incident-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="teacher">Teacher</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject_id">{formData.incident_type === 'student' ? 'Student' : 'Teacher'} ID</Label>
                    <Input
                      id="subject_id"
                      data-testid="incident-subject-id-input"
                      value={formData.subject_id}
                      onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })}
                      required
                      placeholder={`e.g., ${formData.incident_type === 'student' ? 'S001' : 'T001'}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject_name">{formData.incident_type === 'student' ? 'Student' : 'Teacher'} Name</Label>
                    <Input
                      id="subject_name"
                      data-testid="incident-subject-name-input"
                      value={formData.subject_name}
                      onChange={(e) => setFormData({ ...formData, subject_name: e.target.value })}
                      required
                      placeholder="Full name"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="incident-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    placeholder="Describe the incident"
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="severity">Severity</Label>
                    <Select
                      value={formData.severity}
                      onValueChange={(value) => setFormData({ ...formData, severity: value })}
                    >
                      <SelectTrigger data-testid="incident-severity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="action_taken">Action Taken</Label>
                    <Input
                      id="action_taken"
                      data-testid="incident-action-input"
                      value={formData.action_taken}
                      onChange={(e) => setFormData({ ...formData, action_taken: e.target.value })}
                      required
                      placeholder="Action taken"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="follow_up">Follow-up (Optional)</Label>
                  <Input
                    id="follow_up"
                    data-testid="incident-followup-input"
                    value={formData.follow_up}
                    onChange={(e) => setFormData({ ...formData, follow_up: e.target.value })}
                    placeholder="Follow-up actions"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="attachment">Attach Document (Optional)</Label>
                  <div className="flex items-center gap-4">
                    <input
                      id="attachment"
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('attachment').click()}
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {attachmentFile ? attachmentFile.name : 'Choose File'}
                    </Button>
                  </div>
                  {attachmentFile && (
                    <p className="text-sm text-green-600">✓ {attachmentFile.name}</p>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    data-testid="submit-incident-btn" 
                    className="bg-gradient-to-r from-[#667EEA] to-[#764BA2]"
                    disabled={uploadingFile}
                  >
                    {uploadingFile ? 'Uploading...' : 'Submit'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="students" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="students" data-testid="tab-students">
            <GraduationCap className="w-4 h-4 mr-2" />
            Students
          </TabsTrigger>
          <TabsTrigger value="teachers" data-testid="tab-teachers">
            <Users className="w-4 h-4 mr-2" />
            Teachers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="students">
          <Card className="shadow-lg border-none">
            <CardHeader>
              <CardTitle>Student Incident Records</CardTitle>
              <CardDescription>All student disciplinary incidents</CardDescription>
            </CardHeader>
            <CardContent>
              {studentIncidents.length === 0 ? (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-[#718096] mx-auto mb-4" />
                  <p className="text-[#718096]">No student disciplinary incidents found</p>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Action Taken</TableHead>
                        <TableHead>Reported By</TableHead>
                        <TableHead>Attachments</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {studentIncidents.map((incident) => (
                        <TableRow key={incident.id} className="table-row" data-testid="incident-row">
                          <TableCell className="font-medium">{incident.date}</TableCell>
                          <TableCell>{incident.subject_name}</TableCell>
                          <TableCell className="max-w-xs truncate">{incident.description}</TableCell>
                          <TableCell>
                            <span className={`badge badge-${incident.severity}`} data-testid={`severity-${incident.severity}`}>
                              {incident.severity}
                            </span>
                          </TableCell>
                          <TableCell>{incident.action_taken}</TableCell>
                          <TableCell className="text-[#718096]">{incident.reported_by}</TableCell>
                          <TableCell>
                            {incident.attachments && incident.attachments.length > 0 && (
                              <span className="flex items-center gap-1 text-[#667EEA]">
                                <FileText className="w-4 h-4" />
                                {incident.attachments.length}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid="view-incident-btn"
                                onClick={() => openViewDialog(incident)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid="edit-incident-btn"
                                onClick={() => openEditDialog(incident)}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              {(user.role === 'admin' || user.role === 'board_member') && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid="delete-incident-btn"
                                  onClick={() => handleDelete(incident.id)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="teachers">
          <Card className="shadow-lg border-none">
            <CardHeader>
              <CardTitle>Teacher Incident Records</CardTitle>
              <CardDescription>All teacher disciplinary incidents</CardDescription>
            </CardHeader>
            <CardContent>
              {teacherIncidents.length === 0 ? (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-[#718096] mx-auto mb-4" />
                  <p className="text-[#718096]">No teacher disciplinary incidents found</p>
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Teacher</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Action Taken</TableHead>
                        <TableHead>Reported By</TableHead>
                        <TableHead>Attachments</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {teacherIncidents.map((incident) => (
                        <TableRow key={incident.id} className="table-row" data-testid="teacher-incident-row">
                          <TableCell className="font-medium">{incident.date}</TableCell>
                          <TableCell>{incident.subject_name}</TableCell>
                          <TableCell className="max-w-xs truncate">{incident.description}</TableCell>
                          <TableCell>
                            <span className={`badge badge-${incident.severity}`}>
                              {incident.severity}
                            </span>
                          </TableCell>
                          <TableCell>{incident.action_taken}</TableCell>
                          <TableCell className="text-[#718096]">{incident.reported_by}</TableCell>
                          <TableCell>
                            {incident.attachments && incident.attachments.length > 0 && (
                              <span className="flex items-center gap-1 text-[#667EEA]">
                                <FileText className="w-4 h-4" />
                                {incident.attachments.length}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid="view-teacher-incident-btn"
                                onClick={() => openViewDialog(incident)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid="edit-teacher-incident-btn"
                                onClick={() => openEditDialog(incident)}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              {(user.role === 'admin' || user.role === 'board_member') && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid="delete-teacher-incident-btn"
                                  onClick={() => handleDelete(incident.id)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      {selectedIncident && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Disciplinary Incident</DialogTitle>
              <DialogDescription>Update incident details</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-subject-id">{selectedIncident.incident_type === 'student' ? 'Student' : 'Teacher'} ID</Label>
                  <Input
                    id="edit-subject-id"
                    data-testid="edit-subject-id-input"
                    value={editFormData.subject_id}
                    onChange={(e) => setEditFormData({ ...editFormData, subject_id: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-subject-name">{selectedIncident.incident_type === 'student' ? 'Student' : 'Teacher'} Name</Label>
                  <Input
                    id="edit-subject-name"
                    data-testid="edit-subject-name-input"
                    value={editFormData.subject_name}
                    onChange={(e) => setEditFormData({ ...editFormData, subject_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  data-testid="edit-description-input"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  required
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-severity">Severity</Label>
                  <Select
                    value={editFormData.severity}
                    onValueChange={(value) => setEditFormData({ ...editFormData, severity: value })}
                  >
                    <SelectTrigger data-testid="edit-severity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-action">Action Taken</Label>
                  <Input
                    id="edit-action"
                    data-testid="edit-action-input"
                    value={editFormData.action_taken}
                    onChange={(e) => setEditFormData({ ...editFormData, action_taken: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-followup">Follow-up</Label>
                <Input
                  id="edit-followup"
                  data-testid="edit-followup-input"
                  value={editFormData.follow_up}
                  onChange={(e) => setEditFormData({ ...editFormData, follow_up: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={editFormData.status}
                  onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}
                >
                  <SelectTrigger data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  data-testid="save-edit-incident-btn"
                  className="bg-gradient-to-r from-[#667EEA] to-[#764BA2]"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* View Dialog */}
      {selectedIncident && (
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>View Disciplinary Incident</DialogTitle>
              <DialogDescription>Incident details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#64748B]">Type</Label>
                  <p className="text-[#0F172A] font-semibold capitalize">{selectedIncident.incident_type}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#64748B]">Date</Label>
                  <p className="text-[#0F172A] font-semibold">{selectedIncident.date}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#64748B]">{selectedIncident.incident_type === 'student' ? 'Student' : 'Teacher'} ID</Label>
                  <p className="text-[#0F172A]">{selectedIncident.subject_id}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#64748B]">{selectedIncident.incident_type === 'student' ? 'Student' : 'Teacher'} Name</Label>
                  <p className="text-[#0F172A]">{selectedIncident.subject_name}</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#64748B]">Description</Label>
                <div className="mt-1 p-3 bg-[#F9FAFB] border border-[#E2E8F0] rounded-md">
                  <p className="text-sm text-[#0F172A]">{selectedIncident.description}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#64748B]">Severity</Label>
                  <p className="mt-1">
                    <span className={`badge badge-${selectedIncident.severity}`}>
                      {selectedIncident.severity}
                    </span>
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#64748B]">Status</Label>
                  <p className="mt-1">
                    <span className={`badge badge-${selectedIncident.status}`}>
                      {selectedIncident.status}
                    </span>
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#64748B]">Action Taken</Label>
                <p className="text-[#0F172A] mt-1">{selectedIncident.action_taken}</p>
              </div>
              {selectedIncident.follow_up && (
                <div>
                  <Label className="text-sm font-medium text-[#64748B]">Follow-up</Label>
                  <p className="text-[#0F172A] mt-1">{selectedIncident.follow_up}</p>
                </div>
              )}
              <div>
                <Label className="text-sm font-medium text-[#64748B]">Reported By</Label>
                <p className="text-[#0F172A] mt-1">{selectedIncident.reported_by}</p>
              </div>
              {selectedIncident.attachments && selectedIncident.attachments.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-[#64748B]">Attachments</Label>
                  <div className="mt-1 flex items-center gap-2 text-[#667EEA]">
                    <FileText className="w-4 h-4" />
                    <span>{selectedIncident.attachments.length} file(s) attached</span>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
